import {ReflexiveRestModel} from 'src/lib/reflexive-model/reflexive-rest-model';
import {FieldType} from 'src/lib/reflexive-model/field-type.enum';
import {AbstractControl, FormGroup, Validators} from '@angular/forms';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {floatValidator} from '../../lib/validator/float.validator';
import {BehaviorSubject, combineLatest, Subscription} from 'rxjs';
import {RestFilterModel} from 'gelios-ng-admin-components';
import {AuthorizationService} from '../authorization/authorization.service';
import {registrationNumberValidator} from '../../lib/validator/registrationNumber.validator';
import {Unit} from '../dashboard/monitoring/unit';
import {UserModel} from '../user/user-list/user.model';
import {UnitModelModel} from '../unit-model/unit-model-list/unit-model.model';
import {UnitHwModelModel} from '../hardware/unit-hw-model/unit-hw-model.model';
import * as L from 'leaflet';
import {numberGreaterThanZeroValidator} from '../../lib/validator/numberGreaterThanZero.validator';
import {clearFields, setMultipleNumberRestFilters, setMultipleRestFilters} from '../utility/form-utility-functions';
import {debounceTime, distinctUntilChanged, skipWhile} from 'rxjs/operators';
import {UnitTypeModel} from '../unit-type/unit-type-list/unit-type.model';

function showOverrideLatLonFields(formGroup) {
  return formGroup
    && formGroup.controls
    && formGroup.controls.enableCoordinateOverride
    && formGroup.controls.enableCoordinateOverride.value;
}

export class UnitReflexiveModel extends ReflexiveRestModel {
  public static readonly TRACKER_REGISTRATION_TYPE_NEW = 'new';
  public static readonly TRACKER_REGISTRATION_TYPE_EXISTING = 'existing';
  public static readonly BATTERY_REGISTRATION_TYPE_NEW = 'new';
  public static readonly BATTERY_REGISTRATION_TYPE_EXISTING = 'existing';
  public static readonly monitoringModuleAttachmentTypeIdInternal = 1;
  public static readonly monitoringModuleAttachmentTypeIdSeparate = 2;
  unitIsMovable: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  unitTypeName = Unit.UNIT_TYPE_ELECTRIC_SCOOTER;
  unitHwModel: UnitHwModelModel;
  unitModel: UnitModelModel;
  colorFieldsArePermitted = false;

  private subscriptions: Subscription[] = [];
  private projectIdFilterValue: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(null);
  private userIdFilterValue: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(null);
  private regionIdFilterValue: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(null);
  private unitModelIdFilterValue: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(null);

  constructor(
    protected http: HttpClient,
    protected loggedInUser: UserModel,
    protected formFieldNames: string[],
    protected unitType: UnitTypeModel,
    unitId,
  ) {
    super();
    this.unitTypeName = this.unitType.name;
    this.colorFieldsArePermitted = this.formFieldNames.includes('html_color') && this.formFieldNames.includes('color_name');
    this.fields = {
      id: {
        type: FieldType.IntegerField,
        label: 'id',
        value: null,
      },
      monitoring_module_attachment_type_id: {
        type: FieldType.ImmutableListField,
        label: 'Monitoring module',
        immutableList: {
          name: 'monitoring-module-attachment-type',
        },
        validators: [Validators.required],
        placeholder: 'Select type',
        onChange: (event, formGroup) => {
          if (event.value === UnitReflexiveModel.monitoringModuleAttachmentTypeIdSeparate) {
            ['hw_model', 'hw_id', 'phone', 'login', 'password']
              .map(fn => formGroup.controls[fn])
              .filter(c => !!c)
              .forEach(c => c.setValue(undefined));
          } else {
            this.unitHwModel = null;
          }
        },
        value: this.unitType.monitoring_module_config_type_id === UnitTypeModel.TYPE_ID_REQUIRE_DISABLED ?
          UnitReflexiveModel.monitoringModuleAttachmentTypeIdSeparate
          : UnitReflexiveModel.monitoringModuleAttachmentTypeIdInternal,
        isVisible: formGroup =>
          this.unitTypeName !== Unit.UNIT_TYPE_TRACKER
          && this.unitTypeName !== Unit.UNIT_TYPE_IOT
          && this.unitType.monitoring_module_config_type_id === UnitTypeModel.TYPE_ID_USER_SELECTABLE,
      },
      project_id: {
        type: FieldType.ForeignSourceField,
        label: 'Project',
        placeholder: 'Select a project',
        value: undefined,
        validators: [Validators.required],
        foreignSource: {
          modelName: 'project',
          keyField: 'id',
          displayedField: 'custom_name',
          filter: {},
          searchModelName: 'ProjectSearch',
          fields: 'id,custom_name',
          filterBehaviorSubject: new BehaviorSubject<RestFilterModel>(null),
          autoSelectIfOnlyOneOption: true,
        },
        onChange: (event, formGroup) => {
          clearFields(this.formGroup, ['legal_entity_id', 'branch_office_id', 'region_id'], []);
          this.projectIdFilterValue.next(event.value);
          formGroup.controls.attachTracker.setValue(false);
          formGroup.controls.attachBattery.setValue(false);
          if (formGroup.value && formGroup.value !== '' && formGroup.controls.region_id) {
            this.filterModels(formGroup, event.value, this.unitTypeName);
            this.filterNewAttachedTrackerUnitModels(null, event.value);
          }
          this.filterHwModels(null, event.value, this.unitTypeName);
          this.filterNewAttachedTrackerHwModels(formGroup, event.value);
          this.filterAdministrators(formGroup, event.value);
          this.filterIntegrators(formGroup, event.value);
        },
        hint: [{description: 'Specify the project in which the object will work'}],
      },
      unit_model_id: {
        type: FieldType.ForeignSourceField,
        label: 'Model',
        placeholder: 'Select a model',
        value: undefined,
        validators: [Validators.required],
        foreignSource: {
          modelName: 'unit-model',
          keyField: 'id',
          displayedField: 'name',
          searchModelName: 'UnitModelSearch',
          fields: 'id,name',
          filterBehaviorSubject: new BehaviorSubject<RestFilterModel>(null),
          autoSelectIfOnlyOneOption: true,
        },
        onChange: (event, formGroup) => {
          this.isMovableModel(event.value);
          clearFields(this.formGroup, ['region_id'], []);
          this.unitModelIdFilterValue.next(event.value);
        },
        onInit: component => {
          this.filterModels(null, this.getFormControlValueForFilter(component.formGroup, 'project_id'), this.unitTypeName);
        },
        hint: [{
          description: 'Specify the object model. The object model is displayed in the client interface in the object card.' +
            ' Depending on the selected model, the functionality may differ.'
        }],
      },
      region_id: {
        type: FieldType.ForeignSourceField,
        label: 'Region',
        placeholder: 'Select region',
        value: undefined,
        foreignSource: {
          modelName: 'region',
          keyField: 'id',
          displayedField: 'name',
          filter: {},
          filterBehaviorSubject: new BehaviorSubject<RestFilterModel>(null),
          fields: 'id,name',
          expand: 'project_custom_name,country_code',
          searchModelName: 'RegionSearch',
          groupBy: (i) => {
            if (i.country_code) {
              return i.country_code;
            }
            return null;
          },
          autoSelectIfOnlyOneOption: true,
        },
        onChange: (event, formGroup) => {
          clearFields(formGroup, ['branch_office_id'], []);
          this.regionIdFilterValue.next(event.value);
        },
        hint: [{
          description: 'Specify the service delivery region for the object.' +
            ' As long as the object\'s service delivery region does not match the object\'s current location,' +
            ' the region name will be outlined in red.'
        }],
      },
      unit_status_id: {
        type: FieldType.ForeignSourceField,
        label: 'Status',
        placeholder: 'Choose a status',
        value: undefined,
        validators: [Validators.required],
        foreignSource: {
          modelName: 'unit-status',
          keyField: 'id',
          displayedField: 'translatedDescription',
          filter: {
            unit_type_name: {
              filterType: 'text',
              type: 'equals',
              filter: this.unitTypeName,
              filterTo: null
            },
          },
          searchModelName: 'UnitStatusSearch',
          fields: 'id,translatedDescription',
        },
        hint: [{
          description: 'The object status depends on the availability of the object in the client application,' +
            ' the activity of the hardware service mode, etc.' +
            ' Please, pay attention that when the object is set to "Available" status,' +
            ' subscription fee for the object in the current month is activated.' +
            ' We recommend not to transfer object to the status "Accessible" before full' +
            ' adjustment and check of the equipment functioning!'
        }],
      },
      name: {
        type: FieldType.StringField,
        label: 'Name',
        placeholder: 'Enter a name',
        value: null,
        validators: [
          (ac: AbstractControl) => this.unitTypeName !== Unit.UNIT_TYPE_IOT ? Validators.required(ac) : null,
        ],
        hint: [{description: 'The name of the object will be displayed in the client interface in the object card'}],
      },
      registration_number_part_provided_by_user: {
        type: FieldType.StringField,
        label: 'Registration number\'s numeric part',
        placeholder: 'Enter registration number\'s numeric part',
        value: null,
        validators: [
          (ac: AbstractControl) => this.unitTypeName === Unit.UNIT_TYPE_IOT ? Validators.required(ac) : null,
        ],
        // hint: [{description: 'The name of the object will be displayed in the client interface in the object card'}],
        isVisible: () => this.unitTypeName === Unit.UNIT_TYPE_IOT,
      },
      hw_model: {
        type: FieldType.ForeignSourceField,
        label: 'HW model',
        placeholder: 'Select a model',
        value: undefined,
        foreignSource: {
          modelName: 'unit-hw-model',
          keyField: 'name',
          displayedField: 'full_name',
          searchModelName: 'UnitHwModelSearch',
          fields: 'name,full_name',
          expand: '',
          filterBehaviorSubject: new BehaviorSubject<RestFilterModel>(null),
          autoSelectIfOnlyOneOption: true,
        },
        isVisible: formGroup => this.monitoringModuleAttachmentTypeIsInternal(formGroup),
        validators: [
          (abstractControl: AbstractControl) => {
            const formGroup = <FormGroup> abstractControl.root;
            if (this.monitoringModuleAttachmentTypeIsInternal(formGroup)) {
              return Validators.required(abstractControl);
            }
            return null;
          }
        ],
        onChange: (event, formGroup) => {
          if (event.value) {
            this.getUnitHwModel(event.value);
          } else {
            this.unitHwModel = null;
          }

          if (this.formGroup) {
            ['attachTracker', 'attachBattery'].map(fn => formGroup.controls[fn]).filter(c => !!c).forEach(c => c.setValue(false));
            ['mechanical_lock_code'].map(fn => formGroup.controls[fn]).filter(c => !!c).forEach(c => c.setValue(''));
          }
        },
        // onInit: component => {
        //   this.filterHwModels(null, this.getFormControlValueForFilter(component.formGroup, 'project_id'), unitTypeName);
        //   if (component.formGroup.controls.hw_model.value) {
        //     this.getUnitHwModel(component.formGroup.controls.hw_model.value, component.formGroup, true);
        //   } else {
        //     this.fields.attachTracker.isVisible = () => false;
        //     this.fields.attachBattery.isVisible = () => false;
        //   }
        // },
        hint: [{
          description: 'Specify the model of telematics equipment installed in your facility.' +
            ' Depending on the selected model of equipment, the correct operation of message reception,' +
            ' sending commands and functional features of the equipment depends on it.'
        }],
      },
      mechanical_lock_code_display: {
        type: FieldType.BooleanField,
        value: null,
        isVisible: () => this.unitHwModel && this.unitHwModel.lock_type === 'mechanical',
      },
      mechanical_lock_code: {
        type: FieldType.StringField,
        label: 'Mechanical lock code',
        placeholder: 'Enter code',
        value: null,
        isVisible: (formGroup: FormGroup) => {
          return this.unitHwModel
            && this.unitHwModel.lock_type === 'mechanical'
            && formGroup
            && formGroup.controls
            && formGroup.controls.mechanical_lock_code_display
            && formGroup.controls.mechanical_lock_code_display.value === true;
        },
        hint: [{
          description: 'Specify the code of the mechanical lock. It is recommended to change the mechanical lock code as often as possible.'
        }],
      },
      hw_id: {
        type: FieldType.StringField,
        label: 'IMEI',
        placeholder: 'Enter IMEI',
        value: null,
        isVisible: formGroup => this.monitoringModuleAttachmentTypeIsInternal(formGroup),
        validators: [
          (abstractControl: AbstractControl) => {
            const formGroup = <FormGroup> abstractControl.root;
            if (this.monitoringModuleAttachmentTypeIsInternal(formGroup)) {
              return Validators.required(abstractControl);
            }
            return null;
          }
        ],
        hint: [{
          description: 'Specify the IMEI of the telematics equipment installed in your facility.' +
            ' Equipment communication with the server depends on the correctness of the specified IMEI.' +
            ' It is recommended to check the correctness of the entered data several times.'
        }],
      },
      phone: {
        type: FieldType.StringField,
        label: 'SIM number',
        placeholder: 'SIM number',
        value: null,
        isVisible: formGroup => this.monitoringModuleAttachmentTypeIsInternal(formGroup),
        validators: [],
        hint: [{description: 'Enter the number of the SIM card installed in the telematics equipment'}],
      },
      login: {
        type: FieldType.StringField,
        label: 'Login',
        placeholder: 'Enter login',
        value: null,
        validators: [],
        isVisible: (formGroup) => this.monitoringModuleAttachmentTypeIsInternal(formGroup) && this.loginPasswordFieldsAreVisible(),
      },
      password: {
        type: FieldType.StringField,
        label: 'Password',
        placeholder: 'Enter password',
        value: null,
        validators: [],
        isVisible: (formGroup) => this.monitoringModuleAttachmentTypeIsInternal(formGroup) && this.loginPasswordFieldsAreVisible(),
      },
      warehouse_number: {
        type: FieldType.StringField,
        label: 'Warehouse number',
        value: null,
        validators: []
      },
      owner: {
        type: FieldType.ImmutableListField,
        immutableList: {
          name: 'unit-owner-type'
        },
        label: 'Owner',
        placeholder: 'Select a role. (Administrator or Integrator)',
        value: undefined,
        isVisible: () => this.ownerFieldIsVisible(),
        validators: [
          (abstractControl: AbstractControl) => {
            if (this.ownerFieldIsVisible()) {
              return Validators.required(abstractControl);
            }
            return null;
          },
        ],
        onChange: (event, formGroup, fieldName) => {
          clearFields(formGroup, ['legal_entity_id', 'branch_office_id'], []);
          const role = event.value;
          if (formGroup && formGroup.controls && formGroup.controls.businessLogicId) {
            formGroup.controls.businessLogicId.setValue(null);
          }
          if (formGroup.controls.integrator_id) {
            formGroup.controls.integrator_id.setValue(null);
          }
          if (formGroup.controls.admin_id) {
            formGroup.controls.admin_id.setValue(null);
          }
        },
        hint: [{description: 'Depending on the selected role, the user selection will be displayed'}],
      },
      admin_id: {
        type: FieldType.ForeignSourceField,
        label: 'User',
        placeholder: 'Select user',
        value: undefined,
        foreignSource: {
          modelName: 'creator',
          keyField: 'id',
          displayedField: 'login',
          filter: {
            role: {
              type: 'equals',
              filterType: 'text',
              filter: 'admin',
              filterTo: null,
            }
          },
          fields: 'id,login',
          searchModelName: 'CreatorSearch',
          filterBehaviorSubject: new BehaviorSubject<RestFilterModel>(null),
        },
        onChange: (event, formGroup, fieldName) => {
          this.filterBusinessModel(event.value);
          clearFields(formGroup, ['legal_entity_id', 'branch_office_id', 'region_id'], []);
          this.userIdFilterValue.next(event.value);
        },
        validators: [
          (abstractControl: AbstractControl) => {
            const formGroup = <FormGroup> abstractControl.root;
            if (
              formGroup.controls
              && formGroup.controls.owner
              && AuthorizationService.roleIsExactlyAdmin(formGroup.controls.owner.value)
            ) {
              return Validators.required(abstractControl);
            }
            return null;
          }
        ],
        isVisible: (formGroup: FormGroup) => !formGroup.controls.owner
          || AuthorizationService.roleIsExactlyAdmin(formGroup.controls.owner.value),
        hint: [{description: 'Depending on the selected role, the user selection will be displayed'}],
      },
      integrator_id: {
        type: FieldType.ForeignSourceField,
        label: 'User',
        placeholder: 'Select user',
        value: undefined,
        foreignSource: {
          modelName: 'creator',
          keyField: 'id',
          displayedField: 'login',
          fields: 'id,login,admin_login',
          searchModelName: 'CreatorSearch',
          groupBy: 'admin_login',
          filterBehaviorSubject: new BehaviorSubject<RestFilterModel>(null),
        },
        validators: [
          (abstractControl: AbstractControl) => {
            const formGroup = <FormGroup> abstractControl.root;
            if (
              formGroup.controls
              && formGroup.controls.owner
              && AuthorizationService.roleIsExactlyIntegrator(formGroup.controls.owner.value)
            ) {
              return Validators.required(abstractControl);
            }
            return null;
          }
        ],
        isVisible: (formGroup: FormGroup) => {
          return !formGroup.controls.owner
            || AuthorizationService.roleIsExactlyIntegrator(formGroup.controls.owner.value);
        },
        onChange: (event, formGroup, fieldName) => {
          clearFields(formGroup, ['legal_entity_id', 'branch_office_id', 'region_id'], []);
          this.userIdFilterValue.next(event.value);
          if (event.value === null || event.value === undefined || event.value === '') {
            formGroup.controls.rent_to_integrator_id.setValue(null);
            formGroup.controls.editable_by_integrator.setValue(false);
          }
          this.filterBusinessModel(event.value);
        },
        hint: [{description: 'Depending on the selected role, the user selection will be displayed'}],
      },
      rent_to_integrator_id: {
        type: FieldType.ForeignSourceField,
        label: 'Rent from integrator to integrator',
        placeholder: 'Select user',
        value: undefined,
        foreignSource: {
          modelName: 'creator',
          keyField: 'id',
          displayedField: 'login',
          filter: {
            role: {
              type: 'equals',
              filterType: 'text',
              filter: 'integrator',
              filterTo: null,
            }
          },
          fields: 'id,login,admin_login',
          searchModelName: 'CreatorSearch',
          groupBy: 'admin_login'
        },
        isVisible: (formGroup: FormGroup) => {
          return formGroup.controls
            && formGroup.controls.integrator_id
            && formGroup.controls.integrator_id.value;
        },
        validators: [],
        hint: [{description: 'Specify the integrator to whom the facility is leased.'}],
      },
      executor_id: {
        type: FieldType.ForeignSourceField,
        label: 'Courier',
        placeholder: 'Select a courier',
        value: undefined,
        foreignSource: {
          modelName: 'executor',
          keyField: 'id',
          displayedField: 'login',
          fields: 'id,login',
          searchModelName: 'ExecutorSearch',
          // filterBehaviorSubject: new BehaviorSubject<RestFilterModel>(null),
        },
        isVisible: () => this.loggedInUser.showCourierFeatures,
      },
      employeeId: {
        type: FieldType.ForeignSourceField,
        label: 'Courier',
        placeholder: 'Select a courier',
        value: undefined,
        foreignSource: {
          modelName: 'employee',
          keyField: 'id',
          displayedField: 'executor_login',
          fields: 'id',
          expand: 'executor_login',
          searchModelName: 'ExecutorSearch',
          // filterBehaviorSubject: new BehaviorSubject<RestFilterModel>(null),
        },
        isVisible: () => this.loggedInUser.showCourierFeatures,
      },

      accessGrantedToExecutorIds: {
        type: FieldType.ForeignSourceField,
        label: 'Co-owners',
        placeholder: 'Select co-owners',
        value: undefined,
        foreignSource: {
          modelName: 'executor',
          keyField: 'id',
          displayedField: 'login',
          fields: 'id,login',
          searchModelName: 'ExecutorSearch',
          multiple: true,
          // filterBehaviorSubject: new BehaviorSubject<RestFilterModel>(null),
        },
        isVisible: () => this.loggedInUser.showCourierFeatures,
      },
      accessGrantedToEmployeeIds: {
        type: FieldType.ForeignSourceField,
        label: 'Co-owners',
        placeholder: 'Select co-owners',
        value: undefined,
        foreignSource: {
          modelName: 'employee',
          keyField: 'id',
          displayedField: 'executor_login',
          fields: 'id',
          expand: 'executor_login',
          searchModelName: 'ExecutorSearch',
          multiple: true,
          // filterBehaviorSubject: new BehaviorSubject<RestFilterModel>(null),
        },
        isVisible: () => this.loggedInUser.showCourierFeatures,
      },
      plate_number: {
        type: FieldType.StringField,
        label: 'Serial number',
        placeholder: 'Enter the serial number of the object',
        value: null,
        validators: [],
        hint: [{
          description: 'The serial number or the state number depending on the selected object type is indicated.' +
            ' This information is only displayed in the admin panel.'
        }],
      },
      registration_number: {
        type: FieldType.StringField,
        label: 'Registration number',
        placeholder: 'Enter registration number',
        value: null,
        validators: [
          (ac: AbstractControl) => this.unitTypeName !== Unit.UNIT_TYPE_IOT ? registrationNumberValidator(ac) : null,
          (ac: AbstractControl) => this.unitTypeName !== Unit.UNIT_TYPE_IOT ? Validators.required(ac) : null,
        ],
        isVisible: formGroup => this.unitTypeName !== Unit.UNIT_TYPE_IOT,
        hint: [{
          description: 'Registration number must correspond to the specified number in the name of the object (numerical value). ' +
            'Based on the specified registration number, the system generates a QR code required for ordering an object by a client. ' +
            'If for some reason the QR code is not readable, the customer enters the registration number manually to start the rental.'
        }],
      },
      color: {
        type: FieldType.BooleanField,
        label: 'Color setting',
        value: false,
        validators: [],
        isVisible: () => this.colorFieldsArePermitted,
        hint: [{description: 'Activate if necessary to specify the color of the object'}],
      },
      html_color: {
        type: FieldType.HtmlColorField,
        label: 'Choose a color',
        value: null,
        isVisible: (formGroup) => this.colorSettingFieldsAreVisible(formGroup),
        validators: [
          (abstractControl: AbstractControl) => {
            const formGroup = <FormGroup> abstractControl.root;
            if (this.colorSettingFieldsAreVisible(formGroup)) {
              return Validators.required(abstractControl);
            }
          }
        ],
        hint: [{description: 'Select the color to get the color code that you will later use in the client interface functionality.'}],
      },
      color_name: {
        type: FieldType.StringField,
        label: 'Description',
        placeholder: 'Enter description',
        value: null,
        isVisible: (formGroup) => this.colorSettingFieldsAreVisible(formGroup),
        validators: [],
        hint: [{description: 'Enter a free-form description of the color, for example, "Beige", "Metallic Green", etc.'}],
      },
      editable_by_integrator: {
        type: FieldType.BooleanField,
        label: 'Right to edit',
        value: null,
        validators: [],
        isVisible: (formGroup: FormGroup) => {
          return formGroup && formGroup.controls && formGroup.controls.integrator_id && formGroup.controls.integrator_id.value;
        },
        hint: [{description: 'Integrator can edit data on the object, as well as delete the object'}],
      },
      showInApplication: {
        type: FieldType.BooleanField,
        label: 'Show in mobile application',
        value: null,
        validators: [],
      },
      mileage: {
        type: FieldType.StringField,
        label: 'Mileage',
        placeholder: 'Enter mileage',
        value: null,
        validators: [Validators.required, floatValidator],
        hint: [{
          description: 'Specify the current mileage of the object.' +
            ' This value will be used to calculate the mileage of the object. '
        }],
      },
      mileagePreviousValue: {
        type: FieldType.HiddenField,
        label: 'Current mileage previouss value',
        value: null,
        validators: []
      },
      attachTracker: {
        type: FieldType.BooleanField,
        label: 'Connect tracker',
        value: false,
        validators: [],
        onChange: (event, formGroup) => {
          if (!event.value) {
            formGroup.get('attachTrackerRegistrationType').setValue(null);
            formGroup.get('existingTrackerId').setValue(null);
            formGroup.get('newAttachedTrackerName').setValue(null);
            formGroup.get('newAttachedTrackerUnitModelId').setValue(null);
            formGroup.get('newAttachedTrackerHwModelName').setValue(null);
            formGroup.get('newAttachedTrackerHwId').setValue(null);
          }
        },
        hint: [{description: ''}],
        isVisible: formGroup => this.unitTypeName !== Unit.UNIT_TYPE_TRACKER && this.unitTypeName !== Unit.UNIT_TYPE_IOT,
      },
      attachTrackerRegistrationType: {
        type: FieldType.ImmutableListField,
        immutableList: {
          name: 'tracker-registration-type'
        },
        label: 'Registration status in the system',
        placeholder: 'Select the status of the registration status in the system',
        value: undefined,
        isVisible: (formGroup) => this.trackerSettingFieldsAreVisible(formGroup),
        validators: [
          (abstractControl: AbstractControl) => {
            const formGroup = <FormGroup> abstractControl.root;
            if (this.trackerSettingFieldsAreVisible(formGroup)) {
              return Validators.required(abstractControl);
            }
            return null;
          }
        ],
        onChange: (event, formGroup, fieldName) => {
          switch (event.value) {
            case  UnitReflexiveModel.TRACKER_REGISTRATION_TYPE_NEW:
              formGroup.controls.existingTrackerId.setValue(null);
              break;
            case UnitReflexiveModel.TRACKER_REGISTRATION_TYPE_EXISTING:
              formGroup.controls.newAttachedTrackerName.setValue(null);
              formGroup.controls.newAttachedTrackerUnitModelId.setValue(null);
              formGroup.controls.newAttachedTrackerHwModelName.setValue(null);
              formGroup.controls.newAttachedTrackerHwId.setValue(null);
              break;
          }
        },
        hint: [{description: ''}],
      },
      existingTrackerId: {
        type: FieldType.TypeaheadField,
        label: 'Trackers',
        placeholder: 'Select trackers',
        value: undefined,
        typeahead: {
          modelName: 'unit',
          keyField: 'id',
          displayedField: 'name',
          filter: {
            isTracker: {
              filterType: 'text',
              type: 'equals',
              filter: 1,
              filterTo: null
            },
            trackerNotAttached: {
              filterType: 'text',
              type: 'equals',
              filter: 1,
              filterTo: null
            },
          },
          searchModelName: 'UnitSearch',
          fields: 'id,name',
        },
        validators: [
          (abstractControl: AbstractControl) => {
            const formGroup = <FormGroup> abstractControl.root;
            if (this.trackerSettingFieldsAreVisible(formGroup)
              && this.existingTrackerSettingFieldsAreVisible(formGroup)) {
              return Validators.required(abstractControl);
            }
            return null;
          }
        ],
        isVisible: (formGroup: FormGroup) => {
          return this.trackerSettingFieldsAreVisible(formGroup)
            && this.existingTrackerSettingFieldsAreVisible(formGroup);
        },
        hint: [{description: ''}],
      },
      newAttachedTrackerName: {
        type: FieldType.StringField,
        label: 'Name',
        placeholder: 'Enter a name',
        value: undefined,
        validators: [(abstractControl: AbstractControl) => {
          const formGroup = <FormGroup> abstractControl.root;
          if (this.trackerSettingFieldsAreVisible(formGroup)
            && this.newTrackerSettingFieldsAreVisible(formGroup)
          ) {
            return Validators.required(abstractControl);
          }
          return null;
        }],
        isVisible: (formGroup: FormGroup) => this.trackerSettingFieldsAreVisible(formGroup)
          && this.newTrackerSettingFieldsAreVisible(formGroup),
        hint: [{description: ''}],
      },
      newAttachedTrackerUnitModelId: {
        type: FieldType.ForeignSourceField,
        label: 'Model',
        placeholder: 'Select a model',
        value: undefined,
        foreignSource: {
          modelName: 'unit-model',
          keyField: 'id',
          displayedField: 'name',
          searchModelName: 'UnitModelSearch',
          fields: 'id,name',
          filterBehaviorSubject: new BehaviorSubject<RestFilterModel>(null),
        },
        validators: [(abstractControl: AbstractControl) => {
          const formGroup = <FormGroup> abstractControl.root;
          if (this.trackerSettingFieldsAreVisible(formGroup)
            && this.newTrackerSettingFieldsAreVisible(formGroup)
          ) {
            return Validators.required(abstractControl);
          }
          return null;
        }],
        onInit: component => {
          this.filterNewAttachedTrackerUnitModels(null, this.getFormControlValueForFilter(component.formGroup, 'project_id'));
        },
        isVisible: (formGroup: FormGroup) => this.trackerSettingFieldsAreVisible(formGroup)
          && this.newTrackerSettingFieldsAreVisible(formGroup),
        hint: [{description: ''}],
      },
      newAttachedTrackerHwModelName: {
        type: FieldType.ForeignSourceField,
        label: 'HW model',
        placeholder: 'Select a model',
        value: undefined,
        foreignSource: {
          modelName: 'unit-hw-model',
          keyField: 'name',
          displayedField: 'full_name',
          searchModelName: 'UnitHwModelSearch',
          fields: 'name,full_name',
          filterBehaviorSubject: new BehaviorSubject<RestFilterModel>(null),
        },
        onInit: component => {
          this.filterNewAttachedTrackerHwModels(null, this.getFormControlValueForFilter(component.formGroup, 'project_id'));
        },
        validators: [(abstractControl: AbstractControl) => {
          const formGroup = <FormGroup> abstractControl.root;
          if (this.trackerSettingFieldsAreVisible(formGroup)
            && this.newTrackerSettingFieldsAreVisible(formGroup)
          ) {
            return Validators.required(abstractControl);
          }
          return null;
        }],
        isVisible: (formGroup: FormGroup) => this.trackerSettingFieldsAreVisible(formGroup)
          && this.newTrackerSettingFieldsAreVisible(formGroup),
        hint: [{
          description: ''
        }],
      },
      newAttachedTrackerHwId: {
        type: FieldType.StringField,
        label: 'Hardware IMEI',
        placeholder: 'Enter IMEI',
        value: undefined,
        validators: [(abstractControl: AbstractControl) => {
          const formGroup = <FormGroup> abstractControl.root;
          if (this.trackerSettingFieldsAreVisible(formGroup)
            && this.newTrackerSettingFieldsAreVisible(formGroup)
          ) {
            return Validators.required(abstractControl);
          }
          return null;
        }],
        isVisible: (formGroup: FormGroup) => this.trackerSettingFieldsAreVisible(formGroup)
          && this.newTrackerSettingFieldsAreVisible(formGroup),
        hint: [{
          description: ''
        }],
      },
      attachBattery: {
        type: FieldType.BooleanField,
        label: 'Connect battery',
        value: false,
        validators: [],
        onChange: (event, formGroup) => {
          if (!event.value) {
            formGroup.get('attachBatteryRegistrationType').setValue(null);
            formGroup.get('existingBatteryId').setValue(null);
            formGroup.get('newAttachedBatteryName').setValue(null);
            formGroup.get('newAttachedBatterySerialNumber').setValue(null);
            formGroup.get('newAttachedBatteryRegistrationNumber').setValue(null);
          }
        },
        hint: [{description: ''}],
        isVisible: formGroup => this.unitHwModel && this.unitHwModel.battery_mount_type === 'removable',
      },
      attachBatteryRegistrationType: {
        type: FieldType.ImmutableListField,
        immutableList: {
          name: 'battery-registration-type'
        },
        label: 'Battery registration status in the system',
        placeholder: 'Select the battery registration status in the system',
        value: undefined,
        isVisible: (formGroup) => this.batterySettingFieldsAreVisible(formGroup),
        validators: [
          (abstractControl: AbstractControl) => {
            const formGroup = <FormGroup> abstractControl.root;
            if (this.batterySettingFieldsAreVisible(formGroup)) {
              return Validators.required(abstractControl);
            }
            return null;
          }
        ],
        onChange: (event, formGroup, fieldName) => {
          switch (event.value) {
            case  UnitReflexiveModel.BATTERY_REGISTRATION_TYPE_NEW:
              formGroup.controls.existingBatteryId.setValue(null);
              break;
            case UnitReflexiveModel.BATTERY_REGISTRATION_TYPE_EXISTING:
              formGroup.controls.newAttachedBatteryName.setValue(null);
              formGroup.controls.newAttachedBatterySerialNumber.setValue(null);
              formGroup.controls.newAttachedBatteryRegistrationNumber.setValue(null);
              break;
          }
        },
        hint: [{description: ''}],
      },
      existingBatteryId: {
        type: FieldType.TypeaheadField,
        label: 'Select battery',
        placeholder: 'Select battery',
        value: undefined,
        typeahead: {
          modelName: 'battery',
          keyField: 'id',
          displayedField: 'name',
          filter: {
            notAttachedToUnit: {
              filterType: 'text',
              type: 'equals',
              filter: 1,
              filterTo: null
            },
            exceptForUnitId: {
              filterType: 'text',
              type: 'equals',
              filter: unitId ? unitId : 0,
              filterTo: null
            }
          },
          searchModelName: 'BatterySearch',
          fields: 'id,name',
        },
        validators: [
          (abstractControl: AbstractControl) => {
            const formGroup = <FormGroup> abstractControl.root;
            if (this.batterySettingFieldsAreVisible(formGroup)
              && this.existingBatterySettingFieldsAreVisible(formGroup)
            ) {
              return Validators.required(abstractControl);
            }
            return null;
          }
        ],
        isVisible: (formGroup: FormGroup) => this.batterySettingFieldsAreVisible(formGroup)
          && this.existingBatterySettingFieldsAreVisible(formGroup),
        hint: [{description: ''}],
      },
      newAttachedBatteryName: {
        type: FieldType.StringField,
        label: 'Name',
        placeholder: 'Enter a name',
        value: undefined,
        validators: [(abstractControl: AbstractControl) => {
          const formGroup = <FormGroup> abstractControl.root;
          if (this.batterySettingFieldsAreVisible(formGroup)
            && this.newBatterySettingFieldsAreVisible(formGroup)
          ) {
            return Validators.required(abstractControl);
          }
          return null;
        }],
        isVisible: (formGroup: FormGroup) => this.batterySettingFieldsAreVisible(formGroup)
          && this.newBatterySettingFieldsAreVisible(formGroup),
        hint: [{description: ''}],
      },
      newAttachedBatterySerialNumber: {
        type: FieldType.StringField,
        label: 'Serial number',
        placeholder: 'Enter the serial number of the object',
        value: undefined,
        validators: [(abstractControl: AbstractControl) => {
          const formGroup = <FormGroup> abstractControl.root;
          if (this.batterySettingFieldsAreVisible(formGroup)
            && this.newBatterySettingFieldsAreVisible(formGroup)
          ) {
            return Validators.required(abstractControl);
          }
          return null;
        }],
        isVisible: (formGroup: FormGroup) => this.batterySettingFieldsAreVisible(formGroup)
          && this.newBatterySettingFieldsAreVisible(formGroup),
        hint: [{
          description: ''
        }],
      },
      newAttachedBatteryRegistrationNumber: {
        type: FieldType.StringField,
        label: 'Registration number',
        placeholder: 'Enter registration number',
        value: undefined,
        validators: [(abstractControl: AbstractControl) => {
          const formGroup = <FormGroup> abstractControl.root;
          if (this.batterySettingFieldsAreVisible(formGroup)
            && this.newBatterySettingFieldsAreVisible(formGroup)
          ) {
            return Validators.required(abstractControl);
          }
          return null;
        }],
        isVisible: (formGroup: FormGroup) => this.batterySettingFieldsAreVisible(formGroup)
          && this.newBatterySettingFieldsAreVisible(formGroup),
        hint: [{
          description: ''
        }],
      },
      attachedToType: {
        type: FieldType.HiddenField,
        label: 'attachedToType',
        value: null,
      },
      attached_to_unit_id: {
        type: FieldType.HiddenField,
        label: 'attached_to_unit_id',
        value: null,
      },
      attached_to_battery_id: {
        type: FieldType.HiddenField,
        label: 'attached_to_battery_id',
        value: null,
      },
      enableCoordinateOverride: {
        type: FieldType.BooleanField,
        label: 'Freeze coordinates',
        value: false,
        validators: [],
        isVisible: (formGroup) => this.unitModel &&
          (
            !this.unitModel.is_movable
            || this.unitModel.allow_overriding_coordinates
          ),
        onChange: (event, formGroup) => {
          if (!event.value) {
            formGroup.get('override_lat').setValue(null);
            formGroup.get('override_lon').setValue(null);
          }
        },
        hint: [{
          description: 'Activate if necessary to freeze coordinates of unit at a certain point. ' +
            'The coordinate input field and unit location on the map will appear after activation.'
        }
        ],
      },
      map: {
        type: FieldType.MapUnitLatLngField,
        mapLatLngFieldConfig: {
          icon: () => {
            const unitStatusConnection = this.fields.status_connection.value
              ? this.fields.status_connection.value
              : 'online-recently';
            const unitStatusConnectionClass = 'status-' + unitStatusConnection;
            return L.divIcon({
              className: 'map-unit-icon ' + unitStatusConnectionClass + ' map-unit-icon-' + this.unitTypeName,
              iconSize: [44, 40],
              iconAnchor: [22, 40],
              shadowSize: [0, 0]
            });
          }
        },
        value: undefined,
        onChange: (event, formGroup) => {
          if (event && event.value) {
            formGroup.controls.override_lat_lon.setValue(event.value);
            const latLng = event.value.replaceAll(' ', '').split(',');
            formGroup.controls.override_lat.setValue(latLng[0]);
            formGroup.controls.override_lon.setValue(latLng[1]);
          }
        },
        isVisible: (formGroup) => showOverrideLatLonFields(formGroup),
      },
      override_lat_lon: {
        type: FieldType.StringField,
        label: 'Coordinates',
        value: null,
        isVisible: (formGroup) => showOverrideLatLonFields(formGroup),
        validators: [
          (abstractControl: AbstractControl) => {
            if (showOverrideLatLonFields(abstractControl.root)) {
              return Validators.required(abstractControl);
            }
            return null;
          },
        ],
        hint: [{description: ''}],
        onChange: event => {
          const regex = /^([+-]?\d+\.\d+?),\s*([+-]?\d+\.\d+?)$/;
          const regexResult = regex.exec(event.value);
          if (regexResult !== null) {
            this.formGroup.controls.map.setValue(regexResult[1] + ', ' + regexResult[2]);
          }
        }
      },
      override_lat: {
        type: FieldType.HiddenField,
        label: 'lat',
        value: null,
      },
      override_lon: {
        type: FieldType.HiddenField,
        label: 'lon',
        value: null,
      },
      status_connection: {
        type: FieldType.HiddenField,
        label: 'status_connection',
        value: null,
      },
      businessLogicId: {
        type: FieldType.ForeignSourceField,
        label: 'Business model',
        placeholder: 'Choose a business model',
        value: undefined,
        foreignSource: {
          modelName: 'business-logic',
          keyField: 'id',
          displayedField: 'custom_name',
          fields: 'id,custom_name',
          filterBehaviorSubject: new BehaviorSubject<RestFilterModel>(null),
          searchModelName: 'BusinessLogicSearch',
          autoSelectIfOnlyOneOption: true,
          autoClearIfValueNotInList: true,
        },
        validators: [Validators.required],
        // isVisible: fG => this.businessModelIsVisible(fG),
      },
      branch_office_id: {
        type: FieldType.ForeignSourceField,
        label: 'Branch office',
        placeholder: 'Select branch office',
        value: undefined,
        foreignSource: {
          modelName: 'branch-office',
          keyField: 'id',
          displayedField: 'name',
          fields: 'id,name,project_custom_name',
          groupBy: 'project_custom_name',
          searchModelName: 'BranchOfficeSearch',
          filterBehaviorSubject: new BehaviorSubject<RestFilterModel>(null),
        },
      },
      legal_entity_id: {
        type: FieldType.ForeignSourceField,
        label: 'Legal entity',
        placeholder: 'Select legal entity',
        value: undefined,
        foreignSource: {
          modelName: 'legal-entity',
          keyField: 'id',
          displayedField: 'name',
          fields: 'id,name,project_custom_name',
          groupBy: 'project_custom_name',
          searchModelName: 'LegalEntitySearch',
          filterBehaviorSubject: new BehaviorSubject<RestFilterModel>(null),
        },
      },
      net_cost: {
        type: FieldType.StringField,
        label: 'Net cost',
        placeholder: 'Enter net cost',
        value: undefined,
        validators: [floatValidator, numberGreaterThanZeroValidator],
        isVisible: () => this.loggedInUser.showCourierFeatures,
      },
    };
    this.fieldGroup = [
      {
        name: 'Basic data',
        type: 'group',
        groupContents: [
          {
            name: 'id',
            type: 'field',
          },
          {
            name: 'name',
            type: 'field',
          },
          {
            name: 'registration_number_part_provided_by_user',
            type: 'field',
          },
          {
            name: 'project_id',
            type: 'field',
          },
          {
            name: 'unit_model_id',
            type: 'field',
          },
          {
            name: 'plate_number',
            type: 'field',
          },
          {
            name: 'registration_number',
            type: 'field',
          },
        ],
      },
      {
        name: 'Owner',
        type: 'group',
        groupContents: [
          {
            name: 'owner',
            type: 'field',
          },
          {
            name: 'admin_id',
            type: 'field',
          },
          {
            name: 'integrator_id',
            type: 'field',
          },
          {
            name: 'editable_by_integrator',
            type: 'field',
          },
          {
            name: 'executor_id',
            type: 'field',
          },
          {
            name: 'employeeId',
            type: 'field',
          },
          {
            name: 'accessGrantedToExecutorIds',
            type: 'field',
          },
          {
            name: 'accessGrantedToEmployeeIds',
            type: 'field',
          },
        ],
      },
      {
        name: 'Business model',
        type: 'group',
        groupContents: [
          {
            name: 'businessLogicId',
            type: 'field',
          },
        ],
      },
      {
        name: 'Leasing',
        type: 'group',
        groupContents: [
          {
            name: 'rent_to_integrator_id',
            type: 'field',
          },
        ],
      },
      {
        name: 'Appointments',
        type: 'group',
        groupContents: [
          {
            name: 'region_id',
            type: 'field',
          },
          {
            name: 'branch_office_id',
            type: 'field',
          },
          {
            name: 'legal_entity_id',
            type: 'field',
          },
        ],
      },
      {
        name: 'Hardware',
        type: 'group',
        groupContents: [
          {
            name: 'monitoring_module_attachment_type_id',
            type: 'field',
          },
          {
            name: 'hw_model',
            type: 'field',
          },
          {
            name: 'mechanical_lock_code',
            type: 'field',
          },
          {
            name: 'hw_id',
            type: 'field',
          },
          {
            name: 'phone',
            type: 'field',
          },
          {
            name: 'login',
            type: 'field',
          },
          {
            name: 'password',
            type: 'field',
          },
          {
            name: 'attachTracker',
            type: 'field',
          },
          {
            name: 'attachTrackerRegistrationType',
            type: 'field',
          },
          {
            name: 'existingTrackerId',
            type: 'field',
          },
          {
            name: 'newAttachedTrackerName',
            type: 'field',
          },
          {
            name: 'newAttachedTrackerUnitModelId',
            type: 'field',
          },
          {
            name: 'newAttachedTrackerHwModelName',
            type: 'field',
          },
          {
            name: 'newAttachedTrackerHwId',
            type: 'field',
          },
        ],
      },
      {
        name: 'Settings',
        type: 'group',
        groupContents: [
          {
            name: 'mileage',
            type: 'field',
          },
          {
            name: 'unit_status_id',
            type: 'field',
          },
          {
            name: 'color',
            type: 'field',
          },
          {
            name: 'attachBattery',
            type: 'field',
          },
          {
            name: 'enableCoordinateOverride',
            type: 'field',
          },
          {
            name: 'net_cost',
            type: 'field',
          },
        ],
      },
      {
        name: 'Color setting',
        type: 'group',
        groupContents: [
          {
            name: 'html_color',
            type: 'field',
          },
          {
            name: 'color_name',
            type: 'field',
          },
        ]
      },
      // {
      //   name: 'Tracker connection',
      //   type: 'group',
      //   groupContents: [
      //     {
      //       name: 'attachTrackerRegistrationType',
      //       type: 'field',
      //     },
      //     {
      //       name: 'existingTrackerId',
      //       type: 'field',
      //     },
      //     {
      //       name: 'newAttachedTrackerName',
      //       type: 'field',
      //     },
      //     {
      //       name: 'newAttachedTrackerUnitModelId',
      //       type: 'field',
      //     },
      //     {
      //       name: 'newAttachedTrackerHwModelName',
      //       type: 'field',
      //     },
      //     {
      //       name: 'newAttachedTrackerHwId',
      //       type: 'field',
      //     },
      //   ],
      // },
      {
        name: 'Battery connection',
        type: 'group',
        groupContents: [
          {
            name: 'attachBatteryRegistrationType',
            type: 'field',
          },
          {
            name: 'existingBatteryId',
            type: 'field',
          },
          {
            name: 'newAttachedBatteryName',
            type: 'field',
          },
          {
            name: 'newAttachedBatterySerialNumber',
            type: 'field',
          },
          {
            name: 'newAttachedBatteryRegistrationNumber',
            type: 'field',
          },
        ],
      },
      {
        name: 'Unit location',
        type: 'group',
        groupContents: [
          {
            name: 'override_lat_lon',
            type: 'field',
          },
          {
            name: 'map',
            type: 'field',
          },
        ],
      }
    ];

    this.subscriptions.push(
      combineLatest(
        this.projectIdFilterValue,
        this.userIdFilterValue,
      ).pipe(
        distinctUntilChanged(
          (a, b) =>
            (a[0] ? a[0] : 0) === (b[0] ? b[0] : 0)
            && (a[1] ? a[1] : 0) === (b[1] ? b[1] : 0)
        ),
        skipWhile(a => !a[0] && !a[1]),
        debounceTime(500),
      ).subscribe(
        ([projectId, userId]) => {
          setMultipleNumberRestFilters(this.fields, 'legal_entity_id', 'foreignSource', {
            project_id: projectId,
            user_id: userId,
          });
        }
      )
    );
    this.subscriptions.push(
      combineLatest(
        this.projectIdFilterValue,
        this.userIdFilterValue,
        this.regionIdFilterValue,
      ).pipe(
        distinctUntilChanged(
          (a, b) =>
            (a[0] ? a[0] : 0) === (b[0] ? b[0] : 0)
            && (a[1] ? a[1] : 0) === (b[1] ? b[1] : 0)
            && (a[2] ? a[2] : 0) === (b[2] ? b[2] : 0)
        ),
        skipWhile(a => !a[0] && !a[1] && !a[2]),
        debounceTime(500),
      ).subscribe(
        ([projectId, userId, regionId]) => {
          setMultipleNumberRestFilters(this.fields, 'branch_office_id', 'foreignSource', {
            project_id: projectId,
            user_id: userId,
            region_id: regionId
          });
        }
      )
    );

    this.subscriptions.push(
      combineLatest(
        this.projectIdFilterValue,
        this.userIdFilterValue,
        this.unitModelIdFilterValue,
      ).pipe(
        distinctUntilChanged(
          (a, b) =>
            (a[0] ? a[0] : 0) === (b[0] ? b[0] : 0)
            && (a[1] ? a[1] : 0) === (b[1] ? b[1] : 0)
            && (a[2] ? a[2] : 0) === (b[2] ? b[2] : 0)
        ),
        skipWhile(a => !a[0] && !a[1] && !a[2]),
        debounceTime(500),
      ).subscribe(
        ([projectId, userId, unitModelId]) => {
          setMultipleRestFilters(
            this.fields,
            'region_id',
            [
              {
                filterName: 'project_id',
                filterType: 'number',
                value: projectId
              },
              {
                filterName: 'unitModelId',
                filterType: 'number',
                value: unitModelId
              },
              {
                filterName: 'user_id',
                filterType: 'number',
                value: userId
              },
            ],
            'foreignSource'
          );
        }
      )
    );
  }

  getUnitHwModel(value) {
    if (!value) {
      this.unitHwModel = null;
      return;
    }
    if (this.unitType.monitoring_module_config_type_id === UnitTypeModel.TYPE_ID_REQUIRE_DISABLED) {
      return null;
    }

    this.http.get(
      environment.apiBasepoint + 'unit-hw-model/' + value,
      {
        observe: 'body'
      }
    ).subscribe(
      (unitHwModel: UnitHwModelModel) => {
        this.unitHwModel = unitHwModel;
      }
    );
  }

  public loadFromServerData(data: any): this {
    this.unitType = data.unitType;
    if ((data.unitType as UnitTypeModel).monitoring_module_config_type_id !== UnitTypeModel.TYPE_ID_REQUIRE_DISABLED) {
      this.unitHwModel = data.hwModel;
    }
    this.unitModel = data.unitModel;
    if (this.unitModel.is_movable) {
      this.fields.enableCoordinateOverride.value = true;
    }
    this.projectIdFilterValue.next(data.project_id);
    this.unitModelIdFilterValue.next(data.unit_model_id);
    // this.getUnitHwModel(data.hw_model);
    const overrideLatLon = data.override_lat + ', ' + data.override_lon;
    if (data.override_lat && data.override_lon) {
      data.override_lat_lon = overrideLatLon;
      data.map = overrideLatLon;
    }
    // this.isMovableModel(data.unit_model_id);
    if (data.integrator_id) {
      data.owner = AuthorizationService.ROLE_INTEGRATOR;
      this.filterBusinessModel(data.integrator_id);
      this.userIdFilterValue.next(data.integrator_id);
    } else {
      if (data.admin_id) {
        data.owner = AuthorizationService.ROLE_ADMIN;
        this.filterBusinessModel(data.admin_id);
        this.userIdFilterValue.next(data.admin_id);
      }
    }
    this.filterAdministrators(null, data.project_id);
    this.filterIntegrators(null, data.project_id);
    this.filterModels(null, data.project_id, data.unit_type_name);
    this.filterNewAttachedTrackerUnitModels(null, data.project_id);
    this.filterHwModels(null, data.project_id, data.unit_type_name);
    this.filterNewAttachedTrackerHwModels(null, data.project_id);
    if (data.html_color || data.color_name) {
      this.fields.color.value = true;
    }
    return super.loadFromServerData(data);
  }

  ownerFieldIsVisible() {
    return AuthorizationService.calculatedRoleHasRightsNonLessThanAdminsManager(this.loggedInUser);
  }

  isMovableModel(modelId) {
    if (modelId) {
      this.http.get<any>(
        environment.apiBasepoint + 'unit-model/' + modelId,
        {observe: 'body'}
      ).subscribe(result => {
        this.unitModel = result;
        if (!this.unitModel.is_movable) {
          this.formGroup.controls.enableCoordinateOverride.setValue(true);
        }
      });
    }
  }

  colorSettingFieldsAreVisible(formGroup) {
    return !!(formGroup && formGroup.controls && formGroup.controls.color && formGroup.controls.color.value);
  }

  trackerSettingFieldsAreVisible(formGroup) {
    return !!(formGroup && formGroup.controls && formGroup.controls.attachTracker && formGroup.controls.attachTracker.value);
  }

  existingTrackerSettingFieldsAreVisible(formGroup) {
    return !!(
      formGroup
      && formGroup.controls
      && formGroup.controls.attachTrackerRegistrationType
      && formGroup.controls.attachTrackerRegistrationType.value === UnitReflexiveModel.TRACKER_REGISTRATION_TYPE_EXISTING
    );
  }

  newTrackerSettingFieldsAreVisible(formGroup) {
    return !!(
      formGroup
      && formGroup.controls
      && formGroup.controls.attachTrackerRegistrationType
      && formGroup.controls.attachTrackerRegistrationType.value === UnitReflexiveModel.TRACKER_REGISTRATION_TYPE_NEW
    );
  }

  batterySettingFieldsAreVisible(formGroup) {
    return this.unitHwModel
      && this.unitHwModel.battery_mount_type === 'removable'
      && formGroup
      && formGroup.controls
      && formGroup.controls.attachBattery
      && formGroup.controls.attachBattery.value
      ;
  }

  existingBatterySettingFieldsAreVisible(formGroup) {
    return formGroup
      && formGroup.controls
      && formGroup.controls.attachBatteryRegistrationType
      && formGroup.controls.attachBatteryRegistrationType.value === UnitReflexiveModel.BATTERY_REGISTRATION_TYPE_EXISTING
      ;
  }

  getFormControlValueForFilter(formGroup: FormGroup, fieldName: string) {
    return formGroup && formGroup.controls
    && formGroup.controls[fieldName]
    && formGroup.controls[fieldName].value ? formGroup.controls[fieldName].value : undefined;
  }

  filterBusinessModel(userId: number | null) {
    // if (formGroup && formGroup.controls && formGroup.controls.businessLogicId) {
    //   formGroup.controls.businessLogicId.setValue(undefined);
    // }
    const filters: RestFilterModel = {};
    if (userId) {
      filters['user_id'] = {
        filterType: 'number',
        type: 'equals',
        filter: userId,
        filterTo: null
      };
    }
    this.fields.businessLogicId.foreignSource.filterBehaviorSubject.next(filters);
  }

  filterAdministrators(formGroup: FormGroup, projectId) {
    if (formGroup && formGroup.controls && formGroup.controls.admin_id) {
      formGroup.controls.admin_id.setValue(null);
    }
    const filters: RestFilterModel = {
      role: {
        filterType: 'number',
        type: 'equals',
        filter: 'admin',
        filterTo: null
      }
    };
    if (projectId) {
      filters['project_id'] = {
        filterType: 'number',
        type: 'equals',
        filter: projectId,
        filterTo: null
      };
    }
    this.fields.admin_id.foreignSource.filterBehaviorSubject.next(filters);
  }

  filterIntegrators(formGroup: FormGroup, projectId) {
    if (formGroup && formGroup.controls && formGroup.controls.integrator_id) {
      formGroup.controls.integrator_id.setValue(null);
    }
    const filters: RestFilterModel = {
      role: {
        filterType: 'number',
        type: 'equals',
        filter: 'integrator',
        filterTo: null
      }
    };
    if (projectId) {
      filters['project_id'] = {
        filterType: 'number',
        type: 'equals',
        filter: projectId,
        filterTo: null
      };
    }
    this.fields.integrator_id.foreignSource.filterBehaviorSubject.next(filters);
  }

  filterModels(formGroup: FormGroup, projectId, unitTypeName) {
    if (formGroup && formGroup.controls && formGroup.controls.unit_model_id) {
      formGroup.controls.unit_model_id.setValue(null);
    }
    const filters: RestFilterModel = {};
    if (projectId) {
      filters['project_id'] = {
        filterType: 'number',
        type: 'equals',
        filter: projectId,
        filterTo: null
      };
    }
    if (unitTypeName) {
      filters['unit_type_name'] = {
        filterType: 'number',
        type: 'equals',
        filter: unitTypeName,
        filterTo: null
      };
    }
    this.fields.unit_model_id.foreignSource.filterBehaviorSubject.next(filters);
  }

  filterNewAttachedTrackerHwModels(formGroup: FormGroup, projectId) {
    if (formGroup && formGroup.controls && formGroup.controls.newAttachedTrackerHwModelName) {
      formGroup.controls.newAttachedTrackerHwModelName.setValue(null);
    }
    const filters: RestFilterModel = {
      isTracker: {
        filterType: 'number',
        type: 'equals',
        filter: 1,
        filterTo: null
      }
    };
    if (projectId) {
      filters['project_id'] = {
        filterType: 'number',
        type: 'equals',
        filter: projectId,
        filterTo: null
      };
    }
    this.fields.newAttachedTrackerHwModelName.foreignSource.filterBehaviorSubject.next(filters);
  }

  filterNewAttachedTrackerUnitModels(formGroup: FormGroup, projectId) {
    if (formGroup && formGroup.controls && formGroup.controls.newAttachedTrackerUnitModelId) {
      formGroup.controls.newAttachedTrackerUnitModelId.setValue(null);
    }
    const filters: RestFilterModel = {
      isTracker: {
        filterType: 'number',
        type: 'equals',
        filter: 1,
        filterTo: null
      }
    };
    if (projectId) {
      filters['project_id'] = {
        filterType: 'number',
        type: 'equals',
        filter: projectId,
        filterTo: null
      };
    }
    this.fields.newAttachedTrackerUnitModelId.foreignSource.filterBehaviorSubject.next(filters);
  }

  filterHwModels(formGroup: FormGroup, projectId, unitTypeName) {
    if (formGroup && formGroup.controls && formGroup.controls.hw_model) {
      formGroup.controls.hw_model.setValue(null);
    }
    const filters: RestFilterModel = {};
    if (projectId) {
      filters['project_id'] = {
        filterType: 'number',
        type: 'equals',
        filter: projectId,
        filterTo: null
      };
    }
    if (unitTypeName) {
      filters['unit_type_name'] = {
        filterType: 'number',
        type: 'equals',
        filter: unitTypeName,
        filterTo: null
      };
    }
    this.fields.hw_model.foreignSource.filterBehaviorSubject.next(filters);
  }

  // businessModelIsVisible(formGroup: FormGroup) {
  //   if (formGroup && formGroup.controls && formGroup.controls.owner.value
  //   ) {
  //     if (AuthorizationService.roleIsExactlyIntegrator(formGroup.controls.owner.value)
  //       && formGroup.controls.integrator_id
  //       && formGroup.controls.integrator_id.value) {
  //       return true;
  //     }
  //     if (AuthorizationService.roleIsExactlyAdmin(formGroup.controls.owner.value)) {
  //       if ((formGroup.controls.admin_id && formGroup.controls.admin_id.value)
  //         || AuthorizationService.roleIsExactlyAdmin(this.loggedInUser.role)) {
  //         return true;
  //       }
  //     }
  //   }
  //   return false;
  // }

  newBatterySettingFieldsAreVisible(formGroup) {
    return !!(
      formGroup
      && formGroup.controls
      && formGroup.controls.attachBatteryRegistrationType
      && formGroup.controls.attachBatteryRegistrationType.value === UnitReflexiveModel.BATTERY_REGISTRATION_TYPE_NEW
    );
  }

  loginPasswordFieldsAreVisible() {
    return this.unitHwModel && (
      this.unitHwModel.hw_type === 'iotbox'
      || this.unitHwModel.hw_type === 'voi_catacomb'
      || this.unitHwModel.hw_type === 'evse'
    );
  }

  monitoringModuleAttachmentTypeIsInternal(formGroup: FormGroup) {
    return formGroup
      && formGroup.controls
      && formGroup.controls.monitoring_module_attachment_type_id
      && formGroup.controls.monitoring_module_attachment_type_id.value === UnitReflexiveModel.monitoringModuleAttachmentTypeIdInternal;
  }

  onDestroy() {
    super.onDestroy();
    this.subscriptions.filter(s => !!s).forEach(s => s.unsubscribe());
  }
}

